import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Eye, ExternalLink, Loader, Search, Filter, CheckCircle, CheckCircle2, XCircle, Clock, MessageSquare, FileText, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { apiClient } from '../../config/api';
import Modal from '../../components/Modal';

const AdminImportedContactPlansPage = () => {
    const navigate = useNavigate();
    const [workflows, setWorkflows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [groupFilter, setGroupFilter] = useState('all');
    const [groups, setGroups] = useState([]);
    
    // Modal state for contact plan details
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedWorkflow, setSelectedWorkflow] = useState(null);
    const [contactPlanDetails, setContactPlanDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [showSql, setShowSql] = useState(false);

    useEffect(() => {
        loadData();
    }, [groupFilter]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [workflowsResult, groupsResult] = await Promise.all([
                apiClient.get('/workflows'),
                apiClient.get('/groups')
            ]);
            
            // Filter to only workflows imported from VoiceBox
            const importedWorkflows = (workflowsResult.workflows || []).filter(w => w.voicebox_contact_plan_id);
            setWorkflows(importedWorkflows);
            
            const sortedGroups = (groupsResult || []).sort((a, b) => a.name.localeCompare(b.name));
            setGroups(sortedGroups);
        } catch (error) {
            console.error('Failed to load imported contact plans:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredWorkflows = workflows.filter(workflow => {
        const matchesSearch = !searchTerm || 
            workflow.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            workflow.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            String(workflow.voicebox_contact_plan_id).includes(searchTerm);
        
        const matchesGroup = groupFilter === 'all' || String(workflow.group_id) === groupFilter;
        
        return matchesSearch && matchesGroup;
    });

    const handleViewDetails = async (workflow) => {
        setSelectedWorkflow(workflow);
        setDetailsModalOpen(true);
        setLoadingDetails(true);
        setContactPlanDetails(null);
        setShowSql(false);

        try {
            const details = await apiClient.get(`/voicebox/workflows/${workflow.id}/contact-plan-details`);
            setContactPlanDetails(details);
        } catch (error) {
            console.error('Failed to load contact plan details:', error);
            setContactPlanDetails({
                error: error.message || 'Failed to load contact plan details'
            });
        } finally {
            setLoadingDetails(false);
        }
    };

    return (
        <div>
            <div className="mb-6">
                <div className="flex justify-between items-start">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                            <Zap size={24} />
                            Imported Contact Plans
                        </h1>
                        <p className="text-gray-600">
                            View contact plans imported from VoiceBox. These have been converted to draft workflows 
                            that can be reviewed, refined, and activated.
                        </p>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border mb-6 p-4">
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search by name, description, or VoiceBox ID..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    <div className="relative">
                        <select
                            value={groupFilter}
                            onChange={(e) => setGroupFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Groups</option>
                            {groups.map(group => {
                                const isSynced = group.is_voicebox_synced === true;
                                return (
                                    <option key={group.id} value={group.id}>
                                        {group.name}{!isSynced && ' (Not Synced)'}
                                    </option>
                                );
                            })}
                        </select>
                        {groupFilter && groupFilter !== 'all' && (() => {
                            const selectedGroup = groups.find(g => g.id === groupFilter);
                            const isSynced = selectedGroup?.is_voicebox_synced === true;
                            return isSynced ? (
                                <div className="absolute right-10 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                    <CheckCircle2 size={16} className="text-green-600" />
                                </div>
                            ) : null;
                        })()}
                    </div>
                </div>
            </div>

            {/* Contact Plans List */}
            {loading ? (
                <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                    <Loader className="animate-spin mx-auto" size={32} />
                    <p className="text-gray-600 mt-4">Loading imported contact plans...</p>
                </div>
            ) : filteredWorkflows.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                    <Zap className="mx-auto text-gray-400" size={48} />
                    <p className="text-gray-600 mt-4">
                        {workflows.length === 0 
                            ? 'No contact plans have been imported yet. Use the Message Suites page to import contact plans from VoiceBox.'
                            : 'No imported contact plans match your search criteria.'}
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">VoiceBox ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredWorkflows.map((workflow) => (
                                <tr key={workflow.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                        <div className="font-medium text-gray-900 flex items-center gap-2">
                                            {workflow.name}
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                VB
                                            </span>
                                        </div>
                                        {workflow.description && (
                                            <div className="text-sm text-gray-500 mt-1 whitespace-pre-line">{workflow.description}</div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="text-sm font-mono text-gray-700">
                                            #{workflow.voicebox_contact_plan_id}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900">
                                        {workflow.group_name || 'All Groups'}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                            workflow.status === 'active' ? 'bg-green-100 text-green-800' :
                                            workflow.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-gray-100 text-gray-800'
                                        }`}>
                                            {workflow.status === 'active' && <CheckCircle size={12} />}
                                            {workflow.status === 'paused' && <Clock size={12} />}
                                            {workflow.status === 'draft' && <MessageSquare size={12} />}
                                            {workflow.status || 'draft'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        {new Date(workflow.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handleViewDetails(workflow)}
                                                className="text-green-600 hover:text-green-900 flex items-center gap-1"
                                                title="View Contact Plan Details"
                                            >
                                                <FileText size={18} />
                                                Details
                                            </button>
                                            <button
                                                onClick={() => navigate(`/admin/workflow-builder/${workflow.id}`)}
                                                className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                                                title="Edit Workflow"
                                            >
                                                <Eye size={18} />
                                                Edit
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Contact Plan Details Modal */}
            <Modal
                isOpen={detailsModalOpen}
                onClose={() => {
                    setDetailsModalOpen(false);
                    setSelectedWorkflow(null);
                    setContactPlanDetails(null);
                    setShowSql(false);
                }}
                title={selectedWorkflow ? `Contact Plan: ${selectedWorkflow.name}` : 'Contact Plan Details'}
                size="2xl"
                resizable={true}
            >
                {loadingDetails ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader className="animate-spin" size={32} />
                        <span className="ml-3 text-gray-600">Loading contact plan details...</span>
                    </div>
                ) : contactPlanDetails?.error ? (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-800">
                        <p className="font-medium">Error loading details</p>
                        <p className="text-sm mt-1">{contactPlanDetails.error}</p>
                    </div>
                ) : contactPlanDetails ? (
                    <div className="space-y-6">
                        {/* AI Summary Section - Prominent */}
                        {contactPlanDetails.aiSummary && (
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                                <div className="flex items-start gap-3">
                                    <div className="flex-shrink-0 mt-1">
                                        <Sparkles className="text-blue-600" size={24} />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                            AI Summary
                                            <span className="text-xs font-normal text-gray-600 bg-white px-2 py-1 rounded">
                                                What this contact plan does
                                            </span>
                                        </h3>
                                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                                            {contactPlanDetails.aiSummary}
                                        </p>
                                        {contactPlanDetails.summaryError && (
                                            <p className="text-xs text-amber-600 mt-2">
                                                Note: {contactPlanDetails.summaryError}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Contact Plan Info */}
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-3">Contact Plan Information</h4>
                            <dl className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <dt className="text-gray-600">VoiceBox ID</dt>
                                    <dd className="font-mono text-gray-900">#{contactPlanDetails.contactPlan?.workflowId}</dd>
                                </div>
                                <div>
                                    <dt className="text-gray-600">Total Triggers</dt>
                                    <dd className="text-gray-900">{contactPlanDetails.stats?.totalTriggers || 0}</dd>
                                </div>
                                <div>
                                    <dt className="text-gray-600">SQL Filters</dt>
                                    <dd className="text-gray-900">{contactPlanDetails.stats?.sqlFiltersCount || 0}</dd>
                                </div>
                                <div>
                                    <dt className="text-gray-600">Group</dt>
                                    <dd className="text-gray-900">{selectedWorkflow?.group_name || 'N/A'}</dd>
                                </div>
                            </dl>
                        </div>

                        {/* SQL Section - Collapsible */}
                        {contactPlanDetails.combinedSql && contactPlanDetails.combinedSql !== '-- No SQL filters found in this contact plan' && (
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <button
                                    onClick={() => setShowSql(!showSql)}
                                    className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                                >
                                    <div className="flex items-center gap-2">
                                        <FileText size={18} className="text-gray-600" />
                                        <span className="font-medium text-gray-900">Original SQL from VoiceBox</span>
                                        <span className="text-xs text-gray-500">
                                            ({contactPlanDetails.sqlStatements?.length || 0} statement{contactPlanDetails.sqlStatements?.length !== 1 ? 's' : ''})
                                        </span>
                                    </div>
                                    {showSql ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                </button>
                                {showSql && (
                                    <div className="p-4 bg-gray-900 text-gray-100 overflow-x-auto">
                                        <pre className="text-sm font-mono whitespace-pre-wrap">
                                            {contactPlanDetails.combinedSql}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* JSON Parameters Section - Show when no SQL exists */}
                        {contactPlanDetails.triggerParametersJson && (
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <button
                                    onClick={() => setShowSql(!showSql)}
                                    className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                                >
                                    <div className="flex items-center gap-2">
                                        <FileText size={18} className="text-gray-600" />
                                        <span className="font-medium text-gray-900">Contact Plan Configuration (JSON)</span>
                                        <span className="text-xs text-gray-500">
                                            (No SQL filters - using JSON configuration)
                                        </span>
                                    </div>
                                    {showSql ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                </button>
                                {showSql && (
                                    <div className="p-4 bg-gray-900 text-gray-100 overflow-x-auto">
                                        <pre className="text-sm font-mono whitespace-pre-wrap">
                                            {contactPlanDetails.triggerParametersJson}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Trigger Details */}
                        {contactPlanDetails.triggers && contactPlanDetails.triggers.length > 0 && (
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-3">Trigger Steps</h4>
                                <div className="space-y-2">
                                    {contactPlanDetails.triggers.map((trigger, idx) => (
                                        <div key={trigger.triggerId} className="border border-gray-200 rounded p-3 bg-white">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="font-medium text-gray-900">
                                                        {idx + 1}. {trigger.title}
                                                    </div>
                                                    <div className="text-sm text-gray-600 mt-1 space-y-1">
                                                        <div>Type: {trigger.actionType}</div>
                                                        {trigger.analysis?.delay && trigger.analysis.delay !== 'immediate' && (
                                                            <div>Delay: {trigger.analysis.delay}</div>
                                                        )}
                                                        {trigger.analysis?.listId && (
                                                            <div>List ID: {trigger.analysis.listId}</div>
                                                        )}
                                                        {trigger.analysis?.messageId && (
                                                            <div>Message ID: {trigger.analysis.messageId}</div>
                                                        )}
                                                        {trigger.analysis?.hasFilters && (
                                                            <div className="text-blue-600">Has filters configured</div>
                                                        )}
                                                    </div>
                                                    {trigger.parameters && typeof trigger.parameters === 'object' && (
                                                        <details className="mt-2">
                                                            <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                                                                View Parameters (JSON)
                                                            </summary>
                                                            <pre className="mt-2 p-2 bg-gray-50 rounded text-xs overflow-x-auto">
                                                                {JSON.stringify(trigger.parameters, null, 2)}
                                                            </pre>
                                                        </details>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex justify-end gap-2 pt-4 border-t">
                            <button
                                onClick={() => {
                                    setDetailsModalOpen(false);
                                    setSelectedWorkflow(null);
                                    setContactPlanDetails(null);
                                    setShowSql(false);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Close
                            </button>
                            <button
                                onClick={() => navigate(`/admin/workflow-builder/${selectedWorkflow?.id}`)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Edit Workflow
                            </button>
                        </div>
                    </div>
                ) : null}
            </Modal>
        </div>
    );
};

export default AdminImportedContactPlansPage;
